import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { resolveAuthGateLayout } from "../../auth/authLayout";
import type { drawButton } from "../ui";
import { mFont, roundedRectPath } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface AuthGateDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  loadingCityBgImage: HTMLImageElement | null;
  titleLogo: HTMLImageElement | null;
  titleLogoReady: boolean;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawAuthGateScreen(deps: AuthGateDeps, model: RenderModel): UiRects {
  const { ctx, canvasHeight: ch, loadingCityBgImage, titleLogo, titleLogoReady, drawBtn, drawHourglass } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const mobile = isMobileViewport();

  // Draw loading city background image (cover-fit)
  if (loadingCityBgImage) {
    const img = loadingCityBgImage;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = CANVAS_WIDTH / ch;
    let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
    if (imgAspect < canvasAspect) {
      const fitH = img.naturalWidth / canvasAspect;
      sy = (img.naturalHeight - fitH) / 2;
      sh = fitH;
    } else {
      const fitW = img.naturalHeight * canvasAspect;
      sx = (img.naturalWidth - fitW) / 2;
      sw = fitW;
    }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, CANVAS_WIDTH, ch);
    // Dark overlay for readability
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, ch);
  }

  // Compute layout using authLayout helper
  const logoDisplayW = mobile ? 110 : 160;
  const logoH = (titleLogoReady && titleLogo != null)
    ? Math.round(logoDisplayW * ((titleLogo.naturalHeight || 40) / (titleLogo.naturalWidth || 200)))
    : 60;
  const layout = resolveAuthGateLayout({
    canvasWidth: CANVAS_WIDTH,
    canvasHeight: ch,
    mobile,
    logoHeight: logoH
  });

  // Title logo
  if (titleLogoReady && titleLogo != null) {
    ctx.save();
    ctx.shadowColor = "rgba(180, 140, 255, 0.6)";
    ctx.shadowBlur = 16;
    ctx.drawImage(titleLogo, layout.logoRect.x, layout.logoRect.y, layout.logoRect.w, layout.logoRect.h);
    ctx.restore();
  }

  // y range for HTML email/password overlay = layout.emailFormRect

  // OR divider — layout positions it with equal gap above and below
  const divY = layout.dividerY;
  const divLineW = 50;
  ctx.strokeStyle = "rgba(130,170,255,0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(centerX - divLineW - 20, divY);
  ctx.lineTo(centerX - 14, divY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(centerX + 14, divY);
  ctx.lineTo(centerX + divLineW + 20, divY);
  ctx.stroke();
  ctx.fillStyle = "#7a9ac8";
  ctx.font = mFont("bold", 7);
  ctx.textAlign = "center";
  ctx.fillText("OR", centerX, divY + 3);

  const loginGoogle: Rect = layout.loginGoogle;
  const loginDiscord: Rect = layout.loginDiscord;

  drawBtn(loginGoogle, "GOOGLE", { fill: "#1a3a5c", stroke: "#4da6ff", variant: "lobby" });
  drawBtn(loginDiscord, "DISCORD", { fill: "#2a1f4c", stroke: "#7289da", variant: "lobby" });

  // Loading spinner overlay on the active auth button
  const loadingAuthMap: Record<string, Rect> = {
    auth_google: loginGoogle,
    auth_discord: loginDiscord,
  };
  const loadingRect = model.loadingAction != null ? loadingAuthMap[model.loadingAction] : undefined;
  if (loadingRect != null) {
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.5)";
    roundedRectPath(ctx, loadingRect.x, loadingRect.y, loadingRect.w, loadingRect.h, 9);
    ctx.fill();
    drawHourglass(
      loadingRect.x + loadingRect.w / 2,
      loadingRect.y + loadingRect.h / 2,
      Math.min(loadingRect.h - 6, 20),
      "#ffffff"
    );
    ctx.restore();
  }

  if (model.authError) {
    ctx.fillStyle = "#ff6b6b";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.fillText(model.authError, centerX, layout.authErrorY);
  }

  return { loginGoogle, loginDiscord };
}
